import type { ForbiddenArg } from "./types.ts";

/**
 * The built-in forbiddenArgs list (docs/specs/onboarding-teammates.md §7.2). Defence in depth, not the fence: the node
 * profile and the egress proxy hold even if a flag is missed.
 */
export const BUILTIN_FORBIDDEN_ARGS: readonly ForbiddenArg[] = Object.freeze([
  { flag: "--dangerously-skip-permissions" },
  { flag: "--allow-dangerously-skip-permissions" },
  { flag: "--dangerously-bypass-approvals-and-sandbox" },
  { flag: "--dangerously-bypass-hook-trust" },
  { flag: "--yolo" },
  { flag: "-y" },
  { flag: "--trust-all-tools" },
  { flag: "--always-approve" },
  { flag: "--auto-approve" },
  { flag: "--permission-mode", value: "dangerous*" },
  { flag: "--permission-mode", value: "bypassPermissions" },
  { flag: "--approval-mode", value: "yolo" },
  { flag: "-s", value: "danger-full-access" },
  { flag: "--sandbox", value: "danger-full-access" },
  { flag: "-c", value: "sandbox_mode=danger-full-access*" },
]);

/** Short flags that take a value, so `-sVALUE`, `-s=VALUE` and `-cKEY=VALUE` split. `-y` takes none. */
const VALUE_SHORT_FLAGS = ["-s", "-c"];

function stripQuotes(s: string): string {
  const t = s.trim();
  if (t.length >= 2 && (t[0] === '"' || t[0] === "'") && t[t.length - 1] === t[0]) return t.slice(1, -1).trim();
  return t;
}

/** Spec §7.2 "Matching" step 4: trim, strip one pair of quotes, then for a `-c` pair split at the first `=` and clean both sides. */
function pair(v: string): string {
  const eq = v.indexOf("=");
  if (eq < 0) return v;
  return `${stripQuotes(v.slice(0, eq))}=${stripQuotes(v.slice(eq + 1))}`;
}

/**
 * Both readings of a value: with one pair of quotes stripped from the whole (`"k = \"v\""`), and without (`"k"="v"`, where the
 * outer quote characters belong to the key and the value). A rule matches if either does, so neither spelling dodges it.
 */
function normaliseValues(flag: string, raw: string): string[] {
  const whole = stripQuotes(raw);
  return flag === "-c" ? [pair(whole), pair(raw.trim())] : [whole, raw.trim()];
}

type Reading = { flag: string; values: string[] };

/** Every element that looks like a flag, with the value it carries (inline, attached, or the next element). Each element is read on its own, so a value-less flag cannot hide a following one. */
function readFlags(argv: readonly string[]): Reading[] {
  const out: Reading[] = [];
  for (let i = 0; i < argv.length; i++) {
    const el = (argv[i] ?? "").trim();
    if (!el.startsWith("-") || el === "-" || el === "--") continue;
    const next = argv[i + 1];
    const paired = next !== undefined && !next.trim().startsWith("-") ? next : null;
    let flag: string;
    let value: string | null;
    if (el.startsWith("--")) {
      const eq = el.indexOf("=");
      flag = eq < 0 ? el : el.slice(0, eq).trim();
      value = eq < 0 ? paired : el.slice(eq + 1);
      if (flag === "--config") flag = "-c";
    } else {
      const short = VALUE_SHORT_FLAGS.find((f) => el.startsWith(f));
      if (short && el.length > 2) {
        flag = short;
        value = el.slice(2).replace(/^=/, "");
      } else {
        flag = el;
        value = paired;
      }
    }
    out.push({ flag, values: value === null ? [] : normaliseValues(flag, value) });
  }
  return out;
}

function valueMatches(rule: string, value: string): boolean {
  return rule.endsWith("*") ? value.startsWith(rule.slice(0, -1)) : value === rule;
}

/**
 * The first forbidden entry `argv` matches, or null. `argv` is the template argv (before `{prompt}`/`{model}` are
 * substituted), so task text can neither trigger nor dodge the check. Handles `--flag=value`, `--flag value`,
 * attached short forms, `--config`, and quoted or TOML-style `-c key="value"` pairs.
 */
export function findForbiddenArg(argv: readonly string[], list: readonly ForbiddenArg[] = BUILTIN_FORBIDDEN_ARGS): ForbiddenArg | null {
  const readings = readFlags(argv);
  for (const rule of list) {
    const ruleFlag = rule.flag === "--config" ? "-c" : rule.flag;
    for (const r of readings) {
      if (r.flag !== ruleFlag) continue;
      if (rule.value === undefined) return rule;
      if (r.values.some((v) => valueMatches(rule.value!, v))) return rule;
    }
  }
  return null;
}

/** A custom definition can add to the built-in list but never remove from it (spec §7.4). */
export function mergeForbiddenArgs(extra: readonly ForbiddenArg[] = []): ForbiddenArg[] {
  const key = (a: ForbiddenArg) => `${a.flag}\0${a.value ?? ""}`;
  const seen = new Set(BUILTIN_FORBIDDEN_ARGS.map(key));
  return [...BUILTIN_FORBIDDEN_ARGS, ...extra.filter((a) => !seen.has(key(a)))];
}

/** The refusal text: names the flag only, never its value. */
export function forbiddenArgMessage(rule: ForbiddenArg): string {
  return `forbidden argument ${rule.flag}${rule.value !== undefined ? " with a forbidden value" : ""}`;
}
